.uic-dialog {
  align-items: center;
  bottom: 0;
  /**
    * @prop --uic-dialog-backdrop-color: color of the overlay background / backdrop. Use rgba to achieve opacity.
    * @prop --uic-dialog-backdrop-filter: `backdrop-filter` used for the overlay background / backdrop.
    * @prop --uic-dialog-content-padding: padding around the dialog panel content. By default large dialogs have not the same value as the other sizes.
    * @prop --uic-dialog-panel-height: height of the dialog panel
    * @prop --uic-dialog-panel-max-height: maxiumum height of the dialog panel
    * @prop --uic-dialog-panel-min-height: minimum height of the dialog panel
    * @prop --uic-dialog-panel-width: width of the dialog panel
    * @prop --uic-dialog-panel-max-width: maximum width of the dialog panel
    * @prop --uic-dialog-panel-header-height: header height
    * @prop --uic-dialog-panel-main-menu-margin: main menu height, the dialog will only cover the area below. This variable is different for different media queries.
    * @prop --uic-dialog-panel-background-color: panel background color
    * @prop --uic-dialog-panel-header-font-size: header font size
    * @prop --uic-dialog-panel-header-font-weight: header font weight
    * @prop --uic-dialog-panel-header-line-height: header line height
    * @prop --uic-dialog-panel-shadow-color: panel shadow color
    * @prop --uic-dialog-z-index: Z-Index of dialog
    */
  display: flex;

  height: calc(100% - var(--uic-dialog-panel-main-menu-margin));
  justify-content: center;
  left: 0;
  max-height: 100vh;
  max-height: calc(100vh - var(--uic-dialog-panel-main-menu-margin));
  max-width: 100vw;
  pointer-events: none;
  position: fixed;
  right: 0;
  top: 0;
  z-index: var(--uic-dialog-z-index);

  &.open {
    display: flex;
  }
}

.uic-dialog__background {
  align-items: center;
  display: flex;
  height: 100%;
  justify-content: center;
  pointer-events: all;
  width: 100%;
  z-index: -1;
}

.uic-dialog__backdrop {
  backdrop-filter: var(--uic-dialog-backdrop-filter, blur(3px));
  background: var(--uic-dialog-backdrop-color, var(--uic-color-whitea50));
  bottom: 0;
  left: 0;
  position: absolute;
  right: 0;
  top: 0;
}

.uic-dialog__panel {
  background: var(--uic-dialog-panel-background-color);
  box-shadow: 0 0 6px 0 var(--uic-dialog-panel-shadow-color);
  flex-direction: column;
  height: auto;

  height: var(--uic-dialog-panel-height, var(--uic-dialog-panel-default-height));
  max-height: var(--uic-dialog-panel-max-height, var(--uic-dialog-panel-default-max-height, 100%));
  max-width: var(--uic-dialog-panel-max-width, var(--uic-dialog-panel-default-max-width, 100%));
  min-height: var(--uic-dialog-panel-min-height,
  min(var(--uic-dialog-panel-default-min-height), calc(100vh - var(--uic-dialog-panel-main-menu-margin))));

  width: var(--uic-dialog-panel-width, var(--uic-dialog-panel-default-width));

  &:focus {
    outline: none;
  }
}

.uic-dialog__trumpf-ui {
  display: flex;
  position: relative;
}

.uic-dialog__panel-header {
  align-items: center;
  box-sizing: border-box;
  display: flex;

  height: var(--uic-dialog-panel-header-height);
  min-height: var(--uic-dialog-panel-header-height);
  padding: 0 4px 0 12px;
  position: relative;

  .uic-dialog__panel-header-inner {
    align-items: center;
    display: flex;
    flex-grow: 1;
    min-width: 0;

    .uic-dialog__panel-header-title {
      @include truncate();
      font-size: var(--uic-dialog-panel-header-font-size);
      font-weight: var(--uic-dialog-panel-header-font-weight);
      line-height: var(--uic-dialog-panel-header-line-height);
    }

    .uic-dialog__panel-header-trailing-icon {
      display: block;
      margin-left: 4px;
    }

    .uic-dialog__panel-header-icon {
      height: var(--uic-icon-height);
      margin-right: calc(var(--uic-icon-width) / 2);
      width: var(--uic-icon-width);
    }

    .uic-dialog__panel-header-close-button {
      margin-left: auto;
    }
  }
}

.uic-dialog__panel-scroll {
  box-shadow: 0 -3px 6px -3px var(--uic-dialog-panel-shadow-color);
  box-sizing: border-box;
  flex: 1;
  height: 100%;
  overflow: auto;
  padding: var(--uic-dialog-content-padding, 14px);
  z-index: 2;

  .simplebar-content {
    height: auto;
  }
}

.uic-dialog__footer {
  z-index: 1;
}

/* Media Queries - dialog sizing */

.uic-dialog {
  @media #{$breakpoint_tablet} {
    --uic-dialog-panel-header-height: 68px;
    --uic-dialog-panel-main-menu-margin: 68px;
    --uic-dialog-panel-footer-height: 52px;

    top: calc(var(--uic-dialog-panel-main-menu-margin) + 1px);

    &--without-main-menu-margin {
      height: 100%;
      max-height: 100vh;
      top: 0;
    }

    &__panel {
      --uic-dialog-panel-default-min-height: 320px;
      --uic-dialog-panel-default-width: 400px;

      &--size-medium-height {
        --uic-dialog-panel-default-min-height: 500px;
      }

      &--size-medium {
        --uic-dialog-panel-default-min-height: 480px;
        --uic-dialog-panel-default-width: 600px;
      }
    }
  }

  @media #{$breakpoint_desktop} {
    --uic-dialog-panel-header-height: 83px;
    --uic-dialog-panel-main-menu-margin: 82px;
    --uic-dialog-panel-footer-height: 62px;

    top: calc(var(--uic-dialog-panel-main-menu-margin) + 2px);

    &--without-main-menu-margin {
      height: 100%;
      max-height: 100vh;
      top: 0;
    }

    &__panel {
      --uic-dialog-panel-default-min-height: 384px;
      --uic-dialog-panel-default-width: 480px;

      &--size-medium-height {
        --uic-dialog-panel-default-min-height: 600px;
      }

      &--size-medium {
        --uic-dialog-panel-default-min-height: 624px;
        --uic-dialog-panel-default-width: 780px;
      }
    }
  }

  @media #{$breakpoint_tablet_small} {
    --uic-dialog-panel-header-height: 51px;
    --uic-dialog-panel-main-menu-margin: 51px;
    --uic-dialog-panel-footer-height: 39px;

    top: calc(var(--uic-dialog-panel-main-menu-margin) + 1px);

    &--without-main-menu-margin {
      height: 100%;
      max-height: 100vh;
      top: 0;
    }

    &__panel {
      --uic-dialog-panel-default-min-height: 240px;
      --uic-dialog-panel-default-width: 300px;

      &--size-medium-height {
        --uic-dialog-panel-default-min-height: 375px;
      }

      &--size-medium {
        --uic-dialog-panel-default-min-height: 360px;
        --uic-dialog-panel-default-width: 450px;
      }
    }
  }

  // Ensure there's enough vertical padding for phones that don't update vh when chrome appears (e.g. iPhone)
  @media only screen and (max-width: 420px) {
    .uic-dialog__panel {
      --uic-dialog-panel-default-max-height: 80vh;
    }
  }

  .uic-dialog__panel--size-large {
    --uic-dialog-panel-default-width: 100%;
    --uic-dialog-panel-default-max-width: min(1024px, 100vw);

    --uic-dialog-panel-default-height: 100%;
    --uic-dialog-panel-default-max-height: min(768px, 100vh);

    .uic-dialog__panel-header {
      padding: 0 16px 0 24px;
    }
  }
}
